import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { stripeCheckoutLink } from '../../redux/actions/myAccountAction'
import { useTrialDays } from '../referral/useTrialDays'
import './trialPlanChoice.css'

interface TrialPlan {
  key: 'standard' | 'pro'
  /** Stripe price lookup key; the backend resolves it to the price ID. */
  priceId: string
  /** The plan's name, shown as is. */
  name: string
  /** `offers` keys. */
  features: string[]
}

const PLANS: TrialPlan[] = [
  {
    key: 'standard',
    priceId: 'standard_monthly',
    name: 'Standard',
    // The active features of the Standard plan (stripe_plans), in learner words.
    features: [
      'offers:trialPlanChoice.standard.dailyLesson',
      'offers:trialPlanChoice.standard.practice',
      'offers:trialPlanChoice.standard.definitions',
      'offers:trialPlanChoice.standard.history',
    ],
  },
  {
    key: 'pro',
    priceId: 'pro_monthly',
    name: 'Pro',
    // What Pro adds (stripe_plans), plus the two 1-on-1 sessions the plan page promises.
    features: [
      'offers:trialPlanChoice.pro.everythingStandard',
      'offers:trialPlanChoice.pro.writing',
      'offers:trialPlanChoice.pro.speaking',
      'offers:trialPlanChoice.pro.calls',
      'offers:trialPlanChoice.pro.history',
    ],
  },
]

/**
 * The free trial (5 days, or 14 for a referred friend) as a choice between Standard and Pro, each with what it includes, so the
 * learner picks the plan instead of being sent to one. Used on the end-of-story screen and on
 * /claim/free-trial (the trial button in story emails). `only` shows a single plan, for links that
 * already made the case for it. The checkout still decides whether the trial applies.
 */
const TrialPlanChoice: React.FC<{ only?: TrialPlan['key'] }> = ({ only }) => {
  const { t } = useTranslation('offers')
  const [starting, setStarting] = useState<TrialPlan['key'] | null>(null)
  const [failed, setFailed] = useState(false)
  const trialDays = useTrialDays()

  const start = async (plan: TrialPlan) => {
    if (starting) return
    setStarting(plan.key)
    setFailed(false)
    try {
      const payload: any = { priceId: plan.priceId, trialDays }
      const affiliateId = (window as any).affiliateId
      if (affiliateId) payload.affiliateId = affiliateId
      const res: any = await stripeCheckoutLink(payload)
      const target = res?.url || res?.return_url
      if (target) {
        window.location.assign(target)
        return
      }
    } catch (e) {
      // shown below
    }
    setFailed(true)
    setStarting(null)
  }

  return (
    <div className='trial-plan-choice'>
      <div className='trial-plan-choice-cards'>
        {PLANS.filter((plan) => !only || plan.key === only).map((plan) => (
          <div key={plan.key} className={`trial-plan-choice-card trial-plan-choice-card--${plan.key}`}>
            <div className='trial-plan-choice-name'>{plan.name}</div>
            <ul className='trial-plan-choice-features'>
              {plan.features.map((feature) => (
                <li key={feature}>{t(feature)}</li>
              ))}
            </ul>
            <button
              type='button'
              className='trial-plan-choice-cta'
              onClick={() => start(plan)}
              disabled={starting !== null}
            >
              {starting === plan.key
                ? t('trialPlanChoice.openingCheckout')
                : t('trialPlanChoice.cta', { plan: plan.name, days: trialDays })}
            </button>
          </div>
        ))}
      </div>
      {only !== 'standard' && <p className='trial-plan-choice-note'>{t('common:teacherLanguageNotice')}</p>}
      {failed && <p className='trial-plan-choice-error'>{t('trialPlanChoice.checkoutError')}</p>}
    </div>
  )
}

export default TrialPlanChoice
